import { QueryClient } from '@tanstack/react-query';
import { Fulfillment } from 'api/models/Fulfillment';
import { ApiService } from 'api/services/ApiService';
import { modelStatusMap } from 'dashboard409a/pages/Operations/components/ModelStatus/ModelStatus.constants';
import { ModelStatus as ModelStatusEnum , ValuationStatus } from 'dashboard409a/pages/Workboard/common/types';
import { FulfillmentQueryKeys } from 'dashboard409a/services/fulfillment';

const CLIENT_DOCS_OR_APPROVAL =
  (label: string) =>
    label === ValuationStatus.CLIENT_DOCS_RECEIVED ||
    label === ValuationStatus.CLIENT_APPROVAL_RECEIVED;

export type FulfillmentSideEffectsParams = {
  statusLabel: string;
  existingFulfillment: Fulfillment | null;
  companyMeasurementDateId: number | undefined;
  appCompanyId: string | undefined;
  /** Supporting due date (e.g. addBusinessDays(new Date(), 4)) - caller provides for consistency. Date or ISO string. */
  supportingDueDate: Date | string;
};

export type FulfillmentSideEffectsResult = {
  action: 'create' | 'update' | 'none';
  createPayload?: Partial<Fulfillment> & {
    company_measurement_date: number;
    model_status: number;
    supporting_due_date: string;
    reviewers: number[];
    preparers: number[];
    is_slc_model: boolean;
  };
  updatePayload?: Partial<Fulfillment>;
};

/**
 * Returns what fulfillment side effects to run after a valuation status change.
 * Used by OperationsModal (with existing data) and by executeFulfillmentSideEffectsForStatusChange (fetches data).
 */
export function getFulfillmentSideEffectsForStatusChange(
  params: FulfillmentSideEffectsParams
): FulfillmentSideEffectsResult {
  const {
    statusLabel,
    existingFulfillment,
    companyMeasurementDateId,
    appCompanyId,
    supportingDueDate
  } = params;

  const isClientDocsOrApproval = CLIENT_DOCS_OR_APPROVAL(statusLabel);

  const supportingDueDateStr =
    supportingDueDate instanceof Date ? supportingDueDate.toISOString() : supportingDueDate;

  if (!existingFulfillment) {
    if (companyMeasurementDateId == null) {
      return { action: 'none' };
    }
    if (isClientDocsOrApproval) {
      if (statusLabel === ValuationStatus.CLIENT_DOCS_RECEIVED) {
        return {
          action: 'create',
          createPayload: {
            company_measurement_date: companyMeasurementDateId,
            model_status: modelStatusMap[ModelStatusEnum.DRAFT],
            supporting_due_date: supportingDueDateStr,
            reviewers: [],
            preparers: [],
            is_slc_model: false,
          },
        };
      } if (statusLabel === ValuationStatus.CLIENT_APPROVAL_RECEIVED) {
        return {
          action: 'create',
          createPayload: {
            company_measurement_date: companyMeasurementDateId,
            model_status: modelStatusMap[ModelStatusEnum.FINAL],
            supporting_due_date: supportingDueDateStr,
            reviewers: [],
            preparers: [],
            is_slc_model: false,
          },
        };
      }
    }
    return {
      action: 'create',
      createPayload: {
        company_measurement_date: companyMeasurementDateId,
        model_status: modelStatusMap[ModelStatusEnum.DRAFT],
        supporting_due_date: supportingDueDateStr,
        reviewers: [],
        preparers: [],
        is_slc_model: !isClientDocsOrApproval,
      },
    };
  }

  if (existingFulfillment && isClientDocsOrApproval && appCompanyId) {
    if (statusLabel === ValuationStatus.CLIENT_DOCS_RECEIVED) {
      return {
        action: 'update',
        updatePayload: {
          model_status: modelStatusMap[ModelStatusEnum.DRAFT],
          supporting_due_date: supportingDueDateStr,
          is_slc_model: false,
        },
      };
    }
    return {
      action: 'update',
      updatePayload: {
        model_status: modelStatusMap[ModelStatusEnum.FINAL],
        supporting_due_date: supportingDueDateStr,
        is_slc_model: false,
      },
    };
  }

  return { action: 'none' };
}

/**
 * Fetches company + fulfillments for the valuation, then runs create or update as needed.
 * Use when changing status from a context that doesn't have fulfillment data (e.g. table row).
 */
export async function executeFulfillmentSideEffectsForStatusChange(
  valuationId: string,
  statusLabel: string,
  options: {
    supportingDueDate: Date | string;
    queryClient?: QueryClient;
  }
): Promise<void> {
  const { supportingDueDate, queryClient } = options;

  const companyMeasurementDate = await ApiService.apiCompanyMeasurementDatesRead(valuationId);
  const companyId = companyMeasurementDate?.company?.id;
  if (!companyId) {
    return;
  }
  const appCompanyId = companyId.toString();

  const fulfillmentResponse = await ApiService.apiDashboardCompanyOperationsFulfillmentList(
    appCompanyId
  );
  const companyMeasurementDateId = companyMeasurementDate.id;
  const existingFulfillment =
    fulfillmentResponse?.results?.find(
      f => f.company_measurement_date === companyMeasurementDateId
    ) ?? null;

  const result = getFulfillmentSideEffectsForStatusChange({
    statusLabel,
    existingFulfillment,
    companyMeasurementDateId,
    appCompanyId,
    supportingDueDate,
  });

  if (result.action === 'create' && result.createPayload) {
    await ApiService.apiDashboardCompanyOperationsFulfillmentCreate(
      appCompanyId,
      result.createPayload as Fulfillment
    );
    if (queryClient) {
      queryClient.invalidateQueries({
        queryKey: [FulfillmentQueryKeys.FULFILLMENT_LIST, appCompanyId],
      });
    }
  } else if (result.action === 'update' && result.updatePayload && appCompanyId) {
    await ApiService.apiDashboardCompanyOperationsFulfillmentPartialUpdate(
      appCompanyId,
      valuationId,
      result.updatePayload as Fulfillment
    );
    if (queryClient) {
      queryClient.invalidateQueries({
        queryKey: [FulfillmentQueryKeys.FULFILLMENT_LIST, appCompanyId],
      });
    }
  }
}
